/*
 * Copyright (c) 2025 curryfirm.com
 */

import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { setWizardLocationId } from '../features/workflow/workflowSlice.js';
import { replaceOrAddLocation } from '../features/locations/locationsSlice.js';
import { PRECISION_OF_LOCATION_ID } from '../constants.js';

/**
 * If location information is passed as parameters to URL...
 * Grab location parameters from the URL.  Construct a location object from them and save it in the
 * locationsSlice's store so that it can be found by street|locationID key later.  Save the key
 * in the wizardLocation member of the workflowSlice's store.
 * @param user Username captured by the authentication process.
 */
const useSaveWizardLocationFromParams = user => {
    const [searchParams] = useSearchParams();
    const dispatch = useDispatch();

    useEffect(() => {
        if (!user) {
            return;  // Wait until authentication finishes.
        }
        const lat = searchParams.get('lat') ?? "";
        const lng = searchParams.get('lng') ?? "";
        const street = searchParams.get('street') ?? "";
        const city = searchParams.get('city') ?? "";
        const geoState = searchParams.get('state') ?? "";
        const zip = searchParams.get('zip') ?? "";

        let locationId = "";
        let key = "";
        let decodedLat = "";
        let decodedLng = "";
        let decodedStreet = "";
        if (lat && lng && street) {
            decodedLat = decodeURIComponent(lat);
            decodedLng = decodeURIComponent(lng);
            const roundedLat = parseFloat(decodedLat).toFixed(PRECISION_OF_LOCATION_ID);
            const roundedLng = parseFloat(decodedLng).toFixed(PRECISION_OF_LOCATION_ID);
            locationId = 'lat' + roundedLat + '_lng' + roundedLng;

            decodedStreet = decodeURIComponent(street);
            key = decodedStreet + '|' + locationId;
        }

        const wizardLocation = {
            key: key,
            customer: user.email ? user.email : user.uid,
            lat: decodedLat,
            lng: decodedLng,
            street: decodedStreet,
            city: city ? decodeURIComponent(city) : null,
            state: geoState ? decodeURIComponent(geoState) : null,
            zip: zip ? decodeURIComponent(zip) : null,
            locationId: locationId,
        };

        if (wizardLocation.lat && wizardLocation.lng) {
            dispatch(replaceOrAddLocation(wizardLocation));
            dispatch(setWizardLocationId(wizardLocation.key));
        }
    }, [user, searchParams, dispatch]);
};

export default useSaveWizardLocationFromParams;
